import { useEffect, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { SectionTitle, Card, Field, Area, SaveButton } from "../ui";

export default function QuizManager() {
  const [quiz, setQuiz] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/quiz").then((res) => setQuiz(res.data));
  }, []);

  const save = async () => {
    setSaving(true);
    const { _id, ...payload } = quiz;
    await api.put("/admin/quiz", payload);
    toast.success("Quiz saved");
    setSaving(false);
  };

  if (!quiz) return <p className="text-charcoal-500">Loading…</p>;

  const setQ = (qi, patch) => {
    const questions = [...quiz.questions];
    questions[qi] = { ...questions[qi], ...patch };
    setQuiz({ ...quiz, questions });
  };
  const setOpt = (qi, oi, patch) => {
    const questions = [...quiz.questions];
    const options = [...questions[qi].options];
    options[oi] = { ...options[oi], ...patch };
    questions[qi] = { ...questions[qi], options };
    setQuiz({ ...quiz, questions });
  };
  const addQuestion = () => setQuiz({ ...quiz, questions: [...quiz.questions, { id: `q${Date.now()}`, text: "New question", options: [{ id: "o1", label: "Option", recommends: [] }] }] });
  const delQuestion = (qi) => setQuiz({ ...quiz, questions: quiz.questions.filter((_, i) => i !== qi) });
  const addOpt = (qi) => setQ(qi, { options: [...quiz.questions[qi].options, { id: `o${Date.now()}`, label: "Option", recommends: [] }] });
  const delOpt = (qi, oi) => setQ(qi, { options: quiz.questions[qi].options.filter((_, i) => i !== oi) });

  return (
    <div data-testid="admin-quiz">
      <SectionTitle title="Quiz manager" subtitle="Edit questions, options, routing slugs, and result text." />
      <div className="space-y-6">
        <Card>
          <div className="space-y-3">
            <Field label="Quiz intro" value={quiz.intro} onChange={(v) => setQuiz({ ...quiz, intro: v })} />
            <Area label="Result intro" value={quiz.result_intro} onChange={(v) => setQuiz({ ...quiz, result_intro: v })} />
            <Area label="Result closing" value={quiz.result_closing} onChange={(v) => setQuiz({ ...quiz, result_closing: v })} />
          </div>
        </Card>

        {quiz.questions.map((q, qi) => (
          <Card key={q.id}>
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs uppercase tracking-wide text-sage-700">Question {qi + 1}</span>
              <button data-testid={`quiz-del-q-${qi}`} onClick={() => delQuestion(qi)} className="text-charcoal-400 hover:text-destructive"><Trash2 size={16} /></button>
            </div>
            <Field testid={`quiz-q-text-${qi}`} value={q.text} onChange={(v) => setQ(qi, { text: v })} />
            <div className="mt-4 space-y-3">
              {q.options.map((o, oi) => (
                <div key={o.id} className="rounded-lg bg-sand-50 p-3 space-y-2">
                  <div className="flex gap-2 items-center">
                    <input
                      data-testid={`quiz-opt-label-${qi}-${oi}`}
                      value={o.label}
                      onChange={(e) => setOpt(qi, oi, { label: e.target.value })}
                      className="flex-1 bg-white border border-sand-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sage/50"
                    />
                    <button onClick={() => delOpt(qi, oi)} className="text-charcoal-400 hover:text-destructive"><Trash2 size={15} /></button>
                  </div>
                  <input
                    placeholder="Recommends slugs (comma separated)"
                    value={(o.recommends || []).join(", ")}
                    onChange={(e) => setOpt(qi, oi, { recommends: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })}
                    className="w-full bg-white border border-sand-200 rounded-lg px-3 py-2 text-xs text-charcoal-500 focus:outline-none focus:ring-2 focus:ring-sage/50"
                  />
                </div>
              ))}
              <button onClick={() => addOpt(qi)} className="inline-flex items-center gap-1 text-sm text-sage-700 hover:text-terracotta"><Plus size={14} /> Add option</button>
            </div>
          </Card>
        ))}

        <button data-testid="quiz-add-question" onClick={addQuestion} className="inline-flex items-center gap-2 rounded-full border border-charcoal-300 px-5 py-2 text-sm font-medium text-charcoal-700 hover:bg-sand-100">
          <Plus size={16} /> Add question
        </button>
        <div><SaveButton testid="quiz-save" onClick={save} saving={saving} /></div>
      </div>
    </div>
  );
}
